import React, { createContext, useContext, useMemo, useRef, useCallback } from "react";
import { WebPubSubClient } from "@azure/web-pubsub-client";
import { apiSlice } from "../api/apiSlice";
import { store } from 'store';
import moment from "moment";
import { INotification } from "models/INotification";

export type HubName = "NotificationHub";

type HubHandle = {
  client: WebPubSubClient;
  startPromise?: Promise<void>;
  lastUsed: number;
  refCount: number;
};

type HubsAPI = {
  ensureHubStarted: (hub: HubName) => Promise<WebPubSubClient>;
  stopHub: (hub: HubName) => Promise<void>;
  joinGroup: (hub: HubName, group: string) => Promise<void>;
  leaveGroup: (hub: HubName, group: string) => Promise<void>;
};

const HubsContext = createContext<HubsAPI | null>(null);

export const useHubs = () => {
  const ctx = useContext(HubsContext);
  if (!ctx) throw new Error("useHubs debe usarse dentro de <HubsProvider>");
  return ctx;
};

export function HubsProvider({ children }: { children: React.ReactNode }) {
  const getClientAccessUrlFor = useCallback(async (hub: HubName) => {
    const args = { hubName: hub };

    // 1️⃣ Check if data is already available in RTK Query cache
    const existing = apiSlice.endpoints.getClientAccess.select(args)(store.getState());
    if (existing?.data?.uri) {
      console.debug(`[${hub}] Using cached client access URI`);
      return existing.data.uri;
    }

    // 2️⃣ Otherwise, trigger a new network request manually
    console.debug(`[${hub}] Fetching new client access URI...`);
    const sub = store.dispatch(apiSlice.endpoints.getClientAccess.initiate(args));

    try {
      const data = await sub.unwrap();
      if (!data?.uri) throw new Error("Response missing 'uri'");
      return data.uri;
    } catch (err) {
      console.error(`[${hub}] Failed to obtain client access:`, err);
      throw err;
    } finally {
      // 3️⃣ Always unsubscribe to prevent leaks
      if (typeof (sub as any).unsubscribe === "function") {
        (sub as any).unsubscribe();
      }
    }
  }, []);

  const hubsRef = useRef<Map<HubName, HubHandle>>(new Map());
  const INACTIVITY_MS = 5 * 60 * 1000;

  async function ensureHubStarted(hub: HubName) {
    let handle = hubsRef.current.get(hub);

    if (!handle) {
      const client = new WebPubSubClient({
        getClientAccessUrl: async () => await getClientAccessUrlFor(hub),
      });

      client.on("disconnected", () => {
        console.debug(`[${hub}] disconnected`);
      });
      client.on("group-message", (e) => {
        const message =
          e.message.data instanceof ArrayBuffer
            ? new TextDecoder().decode(new Uint8Array(e.message.data))
            : e.message.data;

        try {
          const rawNotification = typeof message === "string" ? JSON.parse(message) : message;

          const newNotification: INotification = {
            id: rawNotification.Id,
            destinationUserId: rawNotification.DestinationUserId,
            creationDate: rawNotification.CreationDate,
            stringId: rawNotification.StringId,
            args: rawNotification.Args,
            type: rawNotification.Type,
            action: rawNotification.Action,
            silent: rawNotification.Silent,
            readByClinician: rawNotification.ReadByClinician,
          };

          store.dispatch(
            apiSlice.util.updateQueryData(
              "getNotifications",
              {
                from: null,
                to: moment.utc().endOf("day").toDate().toISOString(),
                readByClinician: null,
              },
              (draft) => {
                const exists = draft.some((n) => n.id === newNotification.id);
                if (!exists) {
                  draft.unshift(newNotification);
                }
              }
            )
          );

          store.dispatch(
            apiSlice.util.updateQueryData(
              "getNotificationsSummary",
              undefined,
              (draft) => {
                if (!newNotification.readByClinician) {
                  draft.notReadByClinicianCount += 1;
                  draft.lastTenNotificationsNotReadByClinician.unshift(newNotification);
                  if (draft.lastTenNotificationsNotReadByClinician.length > 10) {
                    draft.lastTenNotificationsNotReadByClinician.pop();
                  }
                }
              }
            )
          );
        } catch (err) {
          console.error("Error processing WebPubSub message:", err);
        }
      });

      handle = { client, lastUsed: Date.now(), refCount: 0 };
      hubsRef.current.set(hub, handle);
    }

    handle.lastUsed = Date.now();
    handle.refCount++;

    if (!handle.startPromise) {
      handle.startPromise = handle.client.start();
      await handle.startPromise;
    } else {
      await handle.startPromise;
    }

    return handle.client;
  }

  async function stopHub(hub: HubName) {
    const handle = hubsRef.current.get(hub);
    if (!handle) return;

    handle.refCount = Math.max(0, handle.refCount - 1);
    handle.lastUsed = Date.now();

    if (handle.refCount === 0) {
      setTimeout(async () => {
        const curr = hubsRef.current.get(hub);
        if (!curr) return;
        const idle = Date.now() - curr.lastUsed;
        if (curr.refCount === 0 && idle >= INACTIVITY_MS) {
          try {
            await curr.client.stop();
          } catch {
            // ignore errors on stop
          }
          hubsRef.current.delete(hub);
        }
      }, INACTIVITY_MS + 250);
    }
  }

  async function joinGroup(hub: HubName, group: string) {
    const c = await ensureHubStarted(hub);
    await c.joinGroup(group);
  }

  async function leaveGroup(hub: HubName, group: string) {
    const handle = hubsRef.current.get(hub);
    if (!handle) return;
    await handle.client.leaveGroup(group);
  }

  const api = useMemo<HubsAPI>(
    () => ({ ensureHubStarted, stopHub, joinGroup, leaveGroup }),
    []
  );

  return <HubsContext.Provider value={api}>{children}</HubsContext.Provider>;
}
